import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import validate from 'validate.js';
import { makeStyles, propsToClassKey } from '@mui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { enqueueSnackbar } from 'actions';
import { InputBase, colors, Grid, TextField } from '@mui/material';
import { SelectField } from 'components';
import { GetTypeDropdown, SetFormState } from '../../../actions/IncidentTrackerActions';

const schema = {
	genericType: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	type: {
		presence: { allowEmpty: false, message: 'is required' },
	},
	detailedType: {
		presence: { allowEmpty: false, message: 'is required' },
	},
};

const useStyles = makeStyles((theme) => ({
	root: {},
	container: {
		marginTop: theme.spacing(3)
	},
	fields: {
		margin: theme.spacing(-1),
		display: 'flex',
		flexWrap: 'wrap',
		'& > *': {
			flexGrow: 1,
			margin: theme.spacing(1),
		},
	},
	submitButton: {
		marginTop: theme.spacing(2),
		width: '100%',
	},
	divider: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
	noBorder: {
		border: 'none',
	}
}));

const IncidentConfigurationDetailedTypeForm = (props) => {
	const classes = useStyles();
	const {
		onChange,
		disabled,
		genericTypes,
		defaultConfig = undefined,
		...rest
	} = props;
	const dispatch = useDispatch();
	const typeDropdown = useSelector((state) => state.incidentTrackerReducer.typeDropdown);
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.incidentTrackerReducer.formState);
	const [selectedGenericType, setSelectedGenericType] = useState(NaN);
	const [selectedType, setSelectedType] = useState(NaN);
	const [detailedType, setDetailedType] = useState(defaultConfig?.detailedType);
	const id = defaultConfig?.id;

	const _getTypeId = (typeName) => {
		const type = typeDropdown.find(t => t.text === typeName);
		return type?.value;
	};

	const _getGenericTypeId = (genericTypeName) => {
		const genericType = genericTypes.find(genType => genType.text === genericTypeName);
		return genericType?.value;
	};

	useEffect(() => {
		if (defaultConfig?.genericType && Number.isNaN(+defaultConfig?.genericType)) {
			// if the name of the generic type is given, we replace it by its ID
			const genericTypeId = _getGenericTypeId(defaultConfig?.genericType);
			setSelectedGenericType(genericTypeId);
			dispatch(GetTypeDropdown(genericTypeId));
		}
	}, [defaultConfig?.genericType]);

	useEffect(() => {
		const errors = validate(formState.values, schema);
		setFormState({
			...formState,
			isValid: !errors,
			errors: errors || {},
		});
	}, [formState.values]);

	useEffect(() => {
		if (defaultConfig?.type && Number.isNaN(+defaultConfig?.type)) {
			// if the name of the type is given, we replace it by its ID
			const typeId = _getTypeId(defaultConfig?.type);
			setSelectedType(typeId);
		}
	}, [typeDropdown]);

	// Initialize the form state
	useEffect(() => {
		setFormState({
			...formState,
			values: {
				genericType: selectedGenericType,
				type: selectedType,
				detailedType
			}
		});
	}, []);

	const updateFormState = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value,
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});
	};

	const _handleGenericTypeChange = (event) => {
		if (event.target.value != null) {
			setSelectedGenericType(event.target.value);
			dispatch(GetTypeDropdown(event.target.value));
		}
		if (event.target.value !== selectedGenericType) {
			setSelectedType(NaN);
			setFormState({
				...formState,
				values: {
					genericType: selectedGenericType,
					detailedType: formState.values.detailedType
				},
				touched: {
					...formState.touched,
					[event.target.name]: true,
				},
			});
		}
		onChange({ id, dependencyId: selectedType, value: detailedType, name: 'detailedType' });
	};

	const _handleTypeChange = (event) => {
		updateFormState(event);
		setSelectedType(event.target.value);
		onChange({ id, dependencyId: event.target.value, value: detailedType, name: 'detailedType' });
	};

	const _handleDetailedTypeChange = (event) => {
		updateFormState(event);
		setDetailedType(event.target.value);
		onChange({ id, dependencyId: selectedType, value: event.target.value, name: 'detailedType' });
	};

	const hasError = (field) => (!!(formState.touched[field] && formState.errors[field]));

	return (
		<Grid
			className={classes.container}
			spacing="8"
			container
			md={12}
			xs={12}
		>
			<Grid item md={6} xs={6}>
				<SelectField
					id="genericType"
					name="genericType"
					hintText="Generic Type"
					disabled={disabled}
					menuItems={genericTypes || []}
					value={selectedGenericType || ''}
					onChange={_handleGenericTypeChange}
					error={hasError('genericType') ? formState.errors.genericType[0] : null}
					fullWidth
				/>
			</Grid>
			<Grid item md={6} xs={6}>
				<SelectField
					id="type"
					name="type"
					hintText="Type"
					disabled={!selectedGenericType || disabled}
					menuItems={typeDropdown || []}
					value={selectedType || ''}
					onChange={_handleTypeChange}
					error={hasError('type') ? formState.errors.type[0] : null}
					fullWidth
				/>
			</Grid>
			<Grid item md={12} xs={12}>
				<TextField
					id="detailedType"
					name="detailedType"
					label="Detailed Type"
					hintText="Add a value"
					variant="outlined"
					fullWidth
					disabled={!selectedType || disabled}
					value={detailedType || ''}
					onChange={_handleDetailedTypeChange}
					error={hasError('detailedType')}
					helperText={
						hasError('detailedType') ? formState.errors.detailedType[0] : null
					}
				/>
			</Grid>
		</Grid>
	);
};

IncidentConfigurationDetailedTypeForm.propTypes = {
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	genericTypes: PropTypes.array,
	defaultConfig: PropTypes.object,
};

export default IncidentConfigurationDetailedTypeForm;
